import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SetPasswordForm from "../../features/auth/components/SetPasswordForm";
import { AppDispatch, RootState } from "../../common/state/store";
import { ISetPasswordFormData, setInitialPassword } from "../../features/auth/state/authSlice";
import { loadingSelector, LoadingState } from "../../common/state/slice/appStateSlice";
import { jwtSelector } from "../../common/state/selectors/authSelector";

export const SET_PASSWORD_COMPONENTID = "setPasswordComponent";

const SetPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const [setPasswordToken, setSetPasswordToken] = React.useState("");
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [redirecting, setRedirecting] = React.useState(false);

  const { loading, jwt } = useSelector((state: RootState) => {
    return {
      loading: loadingSelector(state, SET_PASSWORD_COMPONENTID),
      jwt: jwtSelector(state),
    };
  });

  // Set language based on URL parameter, ignoring browser language
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const langParam = params.get("lang");

    // Force language from URL, default to French
    const targetLang = langParam === "en" ? "en" : "fr";

    console.log(`[SetPassword] URL lang param: "${langParam}", setting language to: ${targetLang}`);
    i18n.changeLanguage(targetLang);
  }, [location.search, i18n]);

  // Extract code from URL query params
  useEffect(() => {
    const code = new URLSearchParams(location.search).get("code");
    if (code) {
      setSetPasswordToken(code);
    } else {
      // No code provided, show message and redirect
      setRedirecting(true);
      // If already logged in, go to dashboard; otherwise go to login
      setTimeout(() => navigate(jwt ? "/" : "/login"), 2000);
    }
  }, [location.search, navigate, jwt]);

  // Handle success redirect
  useEffect(() => {
    if (loading && loading === LoadingState.Success) {
      // Show success message
      setShowSuccessMessage(true);
      // Success - redirect after 2s
      // If already logged in, go to dashboard; otherwise go to login
      setTimeout(() => navigate(jwt ? "/" : "/login"), 2000);
    }
  }, [loading, navigate, jwt]);

  const handleSubmit = (data: ISetPasswordFormData) => {
    dispatch(
      setInitialPassword({
        componentId: SET_PASSWORD_COMPONENTID,
        code: setPasswordToken,
        ...data,
      }),
    );
  };

  // Show redirect message if no code provided
  if (redirecting) {
    return (
      <div className="bg-slate-100 flex h-screen">
        <div className="flex flex-1 max-w-xs md:max-w-lg mx-auto items-center justify-center">
          <div className="border flex flex-col bg-white border-gray-200 rounded-lg shadow-md py-6 px-8 text-center">
            <p className="text-lg text-gray-700">
              {t(
                "pages.setPassword.invalidLink",
                "Lien invalide. Redirection vers la page de connexion...",
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SetPasswordForm
      onSubmit={handleSubmit}
      loading={loading}
      showSuccessMessage={showSuccessMessage}
    />
  );
};

export default SetPassword;
